import React, { useEffect, useState } from "react";
import styled from "styled-components";
import DOMPurify from "dompurify";
import HeightInterval from "../Components/HeightInterval.jsx";
import Button from "../Components/Button.jsx";
import ModalUnauthorizedUser from "./Modal/ModalUnauthorizedUser.jsx";
import ModalLawCharge from "./Modal/ModalLawCharge.jsx";
import useStore from "../store.js";
import config from "../config.js";
import copy from "../img/copy.png";
import search from "../img/search.png";
import { Desktop, Mobile } from "../MediaQuery/OnboardingPageMediaQuery.js";
import ai from "../img/ai.png";
import dots_png from "../img/dots_png.png";
import dots_gif from "../img/dots_gif.gif";
import { useNavigate } from "react-router-dom";
import { trackNaverComparisonComplete } from "../utils/naverConversion.js";
import { apiFetch } from "../utils/apiClient.js";
import {
  getLowActionConflict,
  LowActionConflictError,
} from "../utils/lowAction.js";

const MAX_IN_PROGRESS_POLLS = 90;

const waitForRetry = async (response, signal) => {
  const retryAfter = Number(response.headers.get("Retry-After"));
  const seconds = Number.isFinite(retryAfter)
    ? Math.min(Math.max(retryAfter, 0), 3)
    : 1;
  await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  if (signal.aborted) {
    const error = new Error("aborted");
    error.name = "AbortError";
    throw error;
  }
};

export const formatComparisonValue = (value) =>
  DOMPurify.sanitize(
    String(value || "")
      .replace(/\\n/g, "\n")
      .replace(/\n/g, "<br />"),
    {
      ALLOWED_TAGS: ["br"],
      ALLOWED_ATTR: [],
    }
  );

const CaseComparison = ({
  caseId,
  isComparison,
  commonality,
  difference,
  onLoadingChange,
  onCopy,
  onSearch,
}) => {
  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryNonce, setRetryNonce] = useState(0);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [showLawChargeModal, setShowLawChargeModal] = useState(false);
  const navigate = useNavigate();
  const { updateUserInfo, updateUserLaw, setLowActionConflict } = useStore();

  useEffect(() => {
    if (!caseId) return undefined;

    if (isComparison && commonality && difference) {
      setComparison({
        commonality: formatComparisonValue(commonality),
        difference: formatComparisonValue(difference),
      });
      setErrorMessage("");
      setIsLoading(false);
      onLoadingChange?.(false);
      return undefined;
    }

    let active = true;
    const controller = new AbortController();

    const loadComparison = async () => {
      setIsLoading(true);
      setErrorMessage("");
      setShowLawChargeModal(false);
      onLoadingChange?.(true);

      try {
        let response;
        for (let attempt = 0; attempt < MAX_IN_PROGRESS_POLLS; attempt += 1) {
          response = await apiFetch(
            `${config.serverUri}/private/query/comparison/${caseId}`,
            {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
            }
          );
          if (response.status !== 202) break;
          await waitForRetry(response, controller.signal);
        }

        const conflict = await getLowActionConflict(response);
        if (conflict) {
          setLowActionConflict(conflict);
          throw new LowActionConflictError(conflict);
        }
        if (!response || response.status === 202) {
          throw new Error("comparison-in-progress-timeout");
        }

        if (response.status === 401 || response.status === 403) {
          if (active) setShowUnauthorizedModal(true);
          throw new Error("unauthorized");
        }
        if (response.status === 402) {
          throw new Error("insufficient-credit");
        }
        if (response.status === 429) {
          throw new Error("rate-limited");
        }
        if (!response.ok) {
          throw new Error(`comparison-request-${response.status}`);
        }
        if (response.status !== 200 && response.status !== 201) {
          throw new Error(`unexpected-comparison-status-${response.status}`);
        }
        const data = await response.json();
        if (!data.commonality || !data.difference) {
          throw new Error("invalid-comparison-response");
        }
        if (!active) return;

        setComparison({
          commonality: formatComparisonValue(data.commonality),
          difference: formatComparisonValue(data.difference),
        });
        if (response.status === 201) {
          try {
            trackNaverComparisonComplete({ id: caseId });
          } catch (trackingError) {
            console.warn("비교 완료 전환 추적에 실패했습니다.", trackingError);
          }
        }

        try {
          await updateUserInfo(setShowUnauthorizedModal);
          await updateUserLaw();
        } catch (refreshError) {
          console.warn("비교 완료 후 사용자 정보 갱신에 실패했습니다.", refreshError);
        }
      } catch (error) {
        if (!active || error.name === "AbortError") return;

        if (error instanceof LowActionConflictError) {
          setErrorMessage("");
        } else if (error.message === "insufficient-credit") {
          setErrorMessage("로우가 부족합니다. 잔액을 확인한 뒤 다시 시도해 주세요.");
          setShowLawChargeModal(true);
        } else if (error.message === "rate-limited") {
          setErrorMessage("비교 요청이 많습니다. 1분 후 다시 시도해 주세요.");
        } else if (error.status === 401 || error.status === 403) {
          setShowUnauthorizedModal(true);
        } else if (error.message !== "unauthorized") {
          setErrorMessage("비교 결과를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
        }
        if (!(error instanceof LowActionConflictError)) {
          console.error("내 상황 비교 요청 실패:", error);
        }
      } finally {
        if (active) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    loadComparison();
    return () => {
      active = false;
      controller.abort();
      onLoadingChange?.(false);
    };
  }, [
    caseId,
    commonality,
    difference,
    isComparison,
    onLoadingChange,
    retryNonce,
    setLowActionConflict,
    updateUserInfo,
    updateUserLaw,
  ]);

  const comparisonContent = comparison && (
    <>
      <IconContainer>
        <Button
          image_src={copy}
          alt_text="복사하기"
          imgWidth={32}
          imgHeight={32}
          onClick={onCopy}
        />
        <Button
          image_src={search}
          alt_text="원문검색"
          imgWidth={32}
          imgHeight={32}
          onClick={onSearch}
        />
      </IconContainer>
      <HeightInterval height={42} />
      <Summary>
        <Title>공통점</Title>
        <Contents dangerouslySetInnerHTML={{ __html: comparison.commonality }} />
      </Summary>
      <HeightInterval height={16} />
      <Summary>
        <Title>차이점</Title>
        <Contents dangerouslySetInnerHTML={{ __html: comparison.difference }} />
      </Summary>
      <HeightInterval height={16} />
      <AlertContents>*AI가 생성한 참고용 비교이며 실제 판단과 다를 수 있습니다.</AlertContents>
      <HeightInterval height={24} />
    </>
  );

  const requestStatus = !comparison && (
    <StatusPanel role={errorMessage ? "alert" : "status"}>
      {isLoading && "판례와 내 상황을 비교하고 있어요."}
      {errorMessage && (
        <>
          <span>{errorMessage}</span>
          <RetryButton type="button" onClick={() => setRetryNonce((value) => value + 1)}>
            다시 시도
          </RetryButton>
        </>
      )}
    </StatusPanel>
  );

  return (
    <>
      <Desktop>
        <Wrapper $height={comparison ? "auto" : "104px"}>
          {requestStatus}
          {comparisonContent}
        </Wrapper>
      </Desktop>
      <Mobile>
        <Wrapper $height={comparison ? "auto" : "124px"}>
          <Overlay>
            <AiDiv>
              <img src={ai} width="21px" height="21px" alt="AI" />
              <AiText>내 상황과 비교하기</AiText>
              <img
                src={isLoading ? dots_gif : dots_png}
                width="22px"
                height="22px"
                alt={isLoading ? "비교 중" : errorMessage ? "비교 실패" : "비교 완료"}
              />
            </AiDiv>
          </Overlay>
          <HeightInterval height={36} />
          {requestStatus}
          {comparisonContent}
        </Wrapper>
      </Mobile>
      {showUnauthorizedModal && (
        <ModalUnauthorizedUser
          isOpen={showUnauthorizedModal}
          onClose={() => {
            setShowUnauthorizedModal(false);
            navigate("/");
          }}
        />
      )}
      <ModalLawCharge
        isOpen={showLawChargeModal}
        onClose={() => setShowLawChargeModal(false)}
      />
    </>
  );
};

const Wrapper = styled.div`
  min-height: ${(props) => props.$height};
  width: 100%;
  background: linear-gradient(
    107deg,
    rgba(0, 243, 112, 0.04) 8.55%,
    rgba(0, 87, 255, 0.04) 73.74%
  );
  position: relative;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  left: 24px;
  bottom: 28px;
  z-index: 10;
  height: 80px;

  img {
    margin-bottom: 8px;
  }

  @media (max-width: 1440px) and (min-width: 701px) {
    height: auto;
    bottom: 17px;
  }

  @media (max-width: 700px) {
    height: auto;
    bottom: 58px;
  }
`;

const StatusPanel = styled.div`
  min-height: 104px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  box-sizing: border-box;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 22px;
  text-align: center;
`;

const RetryButton = styled.button`
  min-width: 88px;
  height: 36px;
  padding: 0 16px;
  border: 1px solid rgba(0, 87, 255, 0.25);
  border-radius: 8px;
  background: #fff;
  color: #0057ff;
  font-weight: 700;
  cursor: pointer;
`;

const Summary = styled.div`
  width: auto;
  display: flex;
  margin-left: 18px;
  margin-right: 32px;

  @media (max-width: 932px) {
    margin-right: 18px;
  }
`;

const Title = styled.div`
  text-align: center;
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  min-width: 40px;
  margin-top: 2.5px;
  margin-right: 10px;
`;

const Contents = styled.div`
  color: #000;
  text-align: justify;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: -0.16px;

  @media (max-width: 932px) {
    font-size: 14px;
    line-height: 24px;
  }
`;

const AlertContents = styled.div`
  color: rgba(0, 0, 0, 0.3);
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  margin-left: 73px;

  @media (max-width: 700px) {
    width: calc(100% - 40px);
    margin: 0 19px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  left: 50%;
  top: -17.5px;
  transform: translateX(-50%);
  z-index: 30;
`;

const AiDiv = styled.div`
  width: 158px;
  height: 32px;
  border-radius: 24px;
  border: 1px solid #f1f1f1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AiText = styled.div`
  color: rgba(0, 0, 0, 0.6);
  text-align: justify;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  margin-left: 6px;
`;

export default CaseComparison;
